import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, Button } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';

interface RoleProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
  fallbackPath?: string;
  showAccessDenied?: boolean;
}

const ROLE_LABELS: Record<string, string> = {
  client: 'клієнтів',
  expert: 'експертів',
  admin: 'адміністраторів',
  developer: 'розробників',
  general: 'усіх користувачів',
};

const AccessDeniedPage: React.FC<{ userRole: string | null; allowedRoles: string[] }> = ({ userRole, allowedRoles }) => {
  const navigate = useNavigate();

  const allowedRolesText = allowedRoles
    .map(role => ROLE_LABELS[role.toLowerCase().trim()] || role)
    .join(', ');

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="60vh"
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          textAlign: 'center',
          maxWidth: 400,
        }}
      >
        <LockIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          Доступ обмежено
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Цей розділ доступний лише для {allowedRolesText}.
          {userRole && (
            <>
              <br />
              Ваша роль: <strong>{userRole}</strong>
            </>
          )}
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/dashboard')}
        >
          На головну
        </Button>
      </Paper>
    </Box>
  );
}

export const RoleProtectedRoute: React.FC<RoleProtectedRouteProps> = ({
                                                                        children,
                                                                        allowedRoles = [],
                                                                        fallbackPath = '/dashboard',
                                                                        showAccessDenied = true,
                                                                      }) => {
  const { user, loading, isAuthenticated } = useAuth();

  // Чекаємо завантаження
  if (loading) {
    return <div>Завантаження...</div>;
  }

  // Якщо не авторизований — редірект на логін
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  // Отримуємо ролі користувача
  const userRoles = user?.roles || [];

  // Якщо немає обмежень по ролям — дозволяємо доступ
  if (!allowedRoles || allowedRoles.length === 0) {
    return <>{children}</>;
  }

  // Нормалізація ролей для порівняння (lowercase, trim)
  const normalizedUserRoles = userRoles.map(r => r.toLowerCase().trim());
  const normalizedAllowedRoles = allowedRoles.map(r => r.toLowerCase().trim());

  // Перевіряємо доступ по ролям
  const hasAccess = normalizedUserRoles.some(role => normalizedAllowedRoles.includes(role));

  if (!hasAccess) {
    if (showAccessDenied) {
      return <AccessDeniedPage userRole={userRoles.length > 0 ? userRoles[0] : null} allowedRoles={allowedRoles} />;
    }
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
};
